// /playback/multi — 多機同步回放（共用時鐘 scrubber，APP#43）。
//
// 選 N 台攝影機 → 單一 time-absolute 共用時鐘驅動全部：拖曳/播放/暫停一次 seek 所有機。
// 每台各自把共用時鐘解析成「該時刻的 clip + 位移」或「該時段無錄影(空缺)」。時鐘是唯一
// 真相來源(advanceClock 依 wall-time × 倍速前進)，各 <video> 為 slave、drift 超限才 re-seek，
// 所以某台緩衝/卡頓不會拖慢整組時間軸。純同步演算法在 ./multicam(有單元測試)；本檔只做
// React↔<video> 接線。段載入(loadFirst/loadMoreRecordings 懶載入)、時間軸數學(./timeline)、scrubber
// (TimelineStrip) 全部沿用單機回放的既有核心。
//
// authz：各機沿用既有 playback 權限——能看單機回放才拉得到該機錄影；某機 403/422 → 該格
// 顯示「無法回放」並排除於同步，其餘機不受影響(不阻斷整頁)。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { useDeviceList } from '../devices/useDeviceList';
import { cameraClass, deviceName, tileState, type DeviceSummary } from '../devices/deviceModel';
import { dayWindowUs, dateFromUsSafe, loadFirstRecordings, loadMoreRecordings, toDateInputValue, type DayRecordingClip } from './playbackLib';
import { TimelineStrip } from './TimelineStrip';
import { coalesceSegments, type TimeRange } from './timeline';
import {
  activeClipIndexAt,
  advanceClock,
  allInGap,
  clipOffsetSec,
  driftExceeds,
  earliestNextClipStart,
  syncTargets,
} from './multicam';
import './playback.css';

const MAX_CAMS = 4; // 2×2；多路同步解碼負擔，v1 上限 4（對齊 LiveGrid 最小版面）
const PLAYBACK_SPEEDS = [0.5, 1, 1.5, 2, 4] as const;
const CLOCK_TICK_MS = 100; // 共用時鐘更新頻率（10Hz：scrubber 夠平滑、成本低）
const timeOnly = (us: number) => formatTimestampUs(us, { year: undefined, month: undefined, day: undefined });

type CamGate = 'forbidden' | 'subscription' | 'error';
interface CamLoad {
  status: 'loading' | 'ready' | 'gated';
  clips: DayRecordingClip[];
  gate?: CamGate;
  /** spec 17 §2.2: this camera's oldest range is loaded → stop pulling older on scrub. */
  reachedStart?: boolean;
}

function urlStillValid(url: string): boolean {
  try {
    const exp = new URL(url).searchParams.get('exp');
    if (!exp) return true;
    return Number(exp) * 1000 > Date.now() + 5000;
  } catch {
    return true;
  }
}

// ---- one camera cell: a <video> slaved to the shared clock ----
interface CamCellProps {
  device: DeviceSummary;
  clips: DayRecordingClip[];
  playheadUs: number | null;
  playing: boolean;
  rate: number;
}

function CamCell({ device, clips, playheadUs, playing, rate }: CamCellProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overrides = useRef(new Map<string, string>());
  const refreshedRef = useRef(false);
  const [src, setSrc] = useState<string | null>(null);
  const [unplayable, setUnplayable] = useState(false);

  // Resolve this camera against the shared clock (its own covering clip + offset, or gap).
  const idx = playheadUs === null ? null : activeClipIndexAt(clips, playheadUs);
  const offsetSec = idx === null ? 0 : clipOffsetSec(clips[idx], playheadUs as number);
  const currentClipId = idx === null ? null : clips[idx].clip_id;
  // Refs so the async <video> event handlers always seek to the LIVE offset/rate/state.
  const offsetRef = useRef(offsetSec);
  offsetRef.current = offsetSec;
  const rateRef = useRef(rate);
  rateRef.current = rate;
  const playingRef = useRef(playing);
  playingRef.current = playing;

  const refresh = useCallback(async (clip: DayRecordingClip): Promise<string | null> => {
    if (!clip.clip_id) return null;
    try {
      const res = await apiFetch<{ url: string }>(`/v3/recordings/${encodeURIComponent(clip.clip_id)}/url`);
      const u = res.data?.url;
      if (!u) return null;
      overrides.current.set(clip.clip_id, u);
      return u;
    } catch {
      return null;
    }
  }, []);

  // Clip under the shared clock changed → (re)load the src (refresh the presigned url if
  // it is already past `exp`). currentClipId is the only dep so we don't reload every tick.
  useEffect(() => {
    let alive = true;
    setUnplayable(false);
    refreshedRef.current = false;
    if (idx === null) {
      setSrc(null);
      return;
    }
    const clip = clips[idx];
    (async () => {
      let url = overrides.current.get(clip.clip_id) ?? clip.url;
      if (!urlStillValid(url)) {
        refreshedRef.current = true;
        const fresh = await refresh(clip);
        if (!fresh) {
          if (alive) setUnplayable(true);
          return;
        }
        url = fresh;
      }
      if (alive) setSrc(url);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClipId]);

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = rateRef.current;
    try {
      v.currentTime = offsetRef.current;
    } catch {
      /* not seekable yet */
    }
    if (playingRef.current) v.play().catch(() => {});
  };

  // Play/pause follows the shared transport.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    if (playing) v.play().catch(() => {});
    else v.pause();
  }, [playing, src]);

  // Keep native rate in sync (loadedmetadata also re-applies it on src swap).
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, [rate, src]);

  // Drift correction: the clock — not this video — owns time. If we've slipped past
  // tolerance (buffering, or the user scrubbed), re-seek to the clock. Tight tolerance
  // when paused/scrubbing for frame-accurate parity; looser while playing to avoid
  // fighting normal decode jitter.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src || idx === null) return;
    const thr = playing ? 0.75 : 0.15;
    if (driftExceeds(v.currentTime, offsetSec, thr)) {
      try {
        v.currentTime = offsetSec;
      } catch {
        /* not seekable yet */
      }
    }
  }, [playheadUs, src, idx, offsetSec, playing]);

  const onError = () => {
    if (idx === null) return;
    if (!refreshedRef.current) {
      refreshedRef.current = true;
      void refresh(clips[idx]).then((u) => {
        if (u) setSrc(u);
        else setUnplayable(true);
      });
    } else {
      setUnplayable(true);
    }
  };

  const label = `${deviceName(device)}${playheadUs !== null && idx !== null ? ` · ${timeOnly(playheadUs)}` : ''}`;

  return (
    <div className="pb-mc-cell" data-testid={`mc-cell-${device.device_id}`}>
      {src && !unplayable ? (
        <video
          ref={videoRef}
          className="pb-mc-video"
          src={src}
          playsInline
          muted
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
          data-testid={`mc-video-${device.device_id}`}
        />
      ) : (
        <div className="pb-mc-video pb-mc-video-empty text-secondary" data-testid={`mc-empty-${device.device_id}`}>
          {unplayable ? t('multiPlayback.cellUnplayable') : idx === null ? t('multiPlayback.cellNoRecording') : t('multiPlayback.cellLoading')}
        </div>
      )}
      <span className="pb-mc-cell-label">{label}</span>
    </div>
  );
}

// ---- per-camera coverage lane (per-gap detail on the shared window) ----
function CoverageLane({ device, clips, range }: { device: DeviceSummary; clips: DayRecordingClip[]; range: TimeRange }) {
  const segs = useMemo(() => coalesceSegments(clips, range), [clips, range]);
  return (
    <div className="pb-mc-lane" data-testid={`mc-lane-${device.device_id}`}>
      <span className="pb-mc-lane-name text-secondary">{deviceName(device)}</span>
      <div className="pb-mc-lane-track" aria-hidden>
        {segs.map((s) => (
          <span key={s.startUs} className="pb-mc-lane-seg" style={{ left: `${s.startFraction * 100}%`, width: `${Math.max(0.3, s.widthFraction * 100)}%` }} />
        ))}
      </div>
    </div>
  );
}

export default function MultiPlaybackPage() {
  const { t } = useTranslation();
  const { devices } = useDeviceList();
  const [searchParams] = useSearchParams();

  const cameras = useMemo(
    () => (devices ?? []).filter((d) => cameraClass(d) === 'camera' && tileState(d) !== 'offline'),
    [devices]
  );

  // Preselect from ?ids= (e.g. arriving from the single-cam page), capped at MAX_CAMS.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || cameras.length === 0) return;
    seededRef.current = true;
    const raw = (searchParams.get('ids') ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    const valid = raw.filter((id) => cameras.some((c) => c.device_id === id)).slice(0, MAX_CAMS);
    if (valid.length > 0) setSelectedIds(valid);
  }, [cameras, searchParams]);

  const seekParam = useMemo(() => {
    const n = Number(searchParams.get('t'));
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);
  const [date, setDate] = useState(() => toDateInputValue(seekParam ? dateFromUsSafe(seekParam) : new Date()));
  const win = useMemo(() => dayWindowUs(date), [date]);

  const [camData, setCamData] = useState<Record<string, CamLoad>>({});
  const [playheadUs, setPlayheadUs] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((m: string) => {
    setToast(m);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const selectedKey = selectedIds.join(',');

  // Load each selected camera's day of recordings (concurrently). Reset transport so a new
  // selection/date starts clean; the init effect below re-anchors the playhead.
  useEffect(() => {
    let alive = true;
    setPlaying(false);
    setPlayheadUs(null);
    if (selectedIds.length === 0) {
      setCamData({});
      return;
    }
    setCamData(() => {
      const next: Record<string, CamLoad> = {};
      for (const id of selectedIds) next[id] = { status: 'loading', clips: [] };
      return next;
    });
    // spec 17 §2.2 lazy model: each camera loads only its first (newest) range on entry;
    // older ranges are pulled on demand when the shared scrubber crosses a camera's boundary.
    for (const id of selectedIds) {
      loadFirstRecordings(id, win)
        .then((res) => {
          if (alive) setCamData((p) => ({ ...p, [id]: { status: 'ready', clips: res.clips, reachedStart: res.reachedStart } }));
        })
        .catch((e: unknown) => {
          if (!alive) return;
          // 回放不受訂閱閘(spec 17 §3.3;後端 media.go 只驗裝置關係)——422 不再視為訂閱失效,落一般 error。403=撤權。
          const gate: CamGate =
            e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error';
          setCamData((p) => ({ ...p, [id]: { status: 'gated', clips: [], gate } }));
        });
    }
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, win.fromUs, win.toUs]);

  // Ready cameras (in selection order) + their clip lists for the shared clock.
  const readyCams = useMemo(
    () =>
      selectedIds
        .map((id) => ({ device: cameras.find((c) => c.device_id === id), load: camData[id] }))
        .filter((x): x is { device: DeviceSummary; load: CamLoad } => !!x.device && x.load?.status === 'ready'),
    [selectedIds, cameras, camData]
  );
  const perCamClips = useMemo(() => readyCams.map((c) => c.load.clips), [readyCams]);
  const perCamKey = readyCams.map((c) => `${c.device.device_id}:${c.load.clips.length}`).join('|');

  // Anchor the playhead once a day's cameras are ready: deep-link ?t= if inside the window,
  // else the earliest footage across cameras (so there's an immediate frame).
  useEffect(() => {
    if (playheadUs !== null || perCamClips.length === 0) return;
    let earliest: number | null = null;
    for (const cl of perCamClips) {
      if (cl.length) {
        const s = cl[0].clip_start_ts;
        if (earliest === null || s < earliest) earliest = s;
      }
    }
    if (earliest === null) return;
    const target = seekParam !== null && seekParam >= win.fromUs && seekParam < win.toUs ? seekParam : earliest;
    setPlayheadUs(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perCamKey, playheadUs]);

  // Refs the interval reads (avoid re-arming the clock on every playhead tick).
  const playheadRef = useRef(playheadUs);
  playheadRef.current = playheadUs;
  const rateRef = useRef(rate);
  rateRef.current = rate;
  const perCamClipsRef = useRef(perCamClips);
  perCamClipsRef.current = perCamClips;

  // Master clock: while playing, advance the shared time by wall-elapsed × rate. If the
  // clock enters a gap for EVERY camera (dead air) jump to the next footage; if the day
  // window ends, stop. This is the single source of truth all cells slave to.
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const iv = setInterval(() => {
      const now = performance.now();
      const elapsed = now - last;
      last = now;
      const cur = playheadRef.current;
      if (cur === null) return;
      const targets = syncTargets(perCamClipsRef.current, cur);
      if (allInGap(targets)) {
        const nxt = earliestNextClipStart(perCamClipsRef.current, cur);
        if (nxt === null) {
          setPlaying(false);
          showToast(t('multiPlayback.toastReachedEnd'));
          return;
        }
        setPlayheadUs(nxt);
        return;
      }
      const step = advanceClock(cur, elapsed, rateRef.current, win);
      setPlayheadUs(step.us);
      if (step.ended) {
        setPlaying(false);
        showToast(t('multiPlayback.toastReachedEnd'));
      }
    }, CLOCK_TICK_MS);
    return () => clearInterval(iv);
  }, [playing, win, showToast, t]);

  const toggle = useCallback((id: string) => {
    setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= MAX_CAMS ? cur : [...cur, id]));
  }, []);

  // Ref so the scrubber handler reads current per-cam load state without re-arming on every tick.
  const camDataRef = useRef(camData);
  camDataRef.current = camData;
  const onSeek = useCallback(
    (us: number) => {
      setPlayheadUs(us);
      // Scrubber-boundary trigger (spec 17 §2.2): if the shared clock crosses before a camera's
      // oldest-loaded clip and older ranges remain, pull that camera's next earlier range.
      for (const id of selectedIds) {
        const cam = camDataRef.current[id];
        if (!cam || cam.status !== 'ready' || cam.reachedStart) continue;
        const oldest = cam.clips[0];
        if (oldest && us < oldest.clip_start_ts) {
          void loadMoreRecordings(id, win)
            .then((res) => {
              setCamData((p) => (p[id] ? { ...p, [id]: { ...p[id], clips: res.clips, reachedStart: res.reachedStart } } : p));
            })
            .catch(() => {
              /* transient — retriggers on the next scrub */
            });
        }
      }
    },
    [selectedIds, win],
  );
  const togglePlay = useCallback(() => {
    if (perCamClipsRef.current.length === 0) return;
    setPlaying((p) => !p);
  }, []);

  const anyReady = readyCams.length > 0;
  const anyLoading = selectedIds.some((id) => camData[id]?.status === 'loading');
  const gatedCams = selectedIds
    .map((id) => ({ device: cameras.find((c) => c.device_id === id), load: camData[id] }))
    .filter((x): x is { device: DeviceSummary; load: CamLoad } => !!x.device && x.load?.status === 'gated');
  const cols = Math.max(1, Math.ceil(Math.sqrt(readyCams.length)));
  // TimelineStrip coalesces the clips it is given → pass every camera's clips so the shared
  // scrubber's segments render the UNION coverage (any camera recording ⇒ footage there).
  const unionRaw = useMemo(() => perCamClips.flat(), [perCamClips]);

  return (
    <section className="pb-page pb-mc-page">
      <header className="pb-header">
        <h1 style={{ margin: 0 }}>{t('multiPlayback.title')}</h1>
        <Link to="/live/grid">{t('multiPlayback.liveGridLink')}</Link>
      </header>
      <p className="text-secondary" style={{ margin: 0 }}>{t('multiPlayback.subtitle', { count: MAX_CAMS })}</p>

      <div className="pb-toolbar">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label={t('playback.datePickerLabel')} className="pb-date" />
        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>{t('multiPlayback.pickLabel', { count: MAX_CAMS })}</span>
      </div>

      {/* camera multi-select（沿用 LiveGrid picker 樣式） */}
      <div className="pb-mc-picker" data-testid="mc-picker">
        {cameras.map((d) => (
          <button
            key={d.device_id}
            onClick={() => toggle(d.device_id)}
            aria-pressed={selectedIds.includes(d.device_id)}
            data-testid={`mc-pick-${d.device_id}`}
            className={selectedIds.includes(d.device_id) ? 'dv-badge dv-badge-rec' : 'dv-badge'}
          >
            {deviceName(d)}
          </button>
        ))}
        {cameras.length === 0 && <p className="text-secondary">{t('multiPlayback.noCameras')}</p>}
      </div>

      {toast && <p className="pb-toast" role="status">{toast}</p>}

      {gatedCams.length > 0 && (
        <p className="text-secondary" role="status" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
          {t('multiPlayback.someUnavailable', { names: gatedCams.map((c) => deviceName(c.device)).join('、') })}
        </p>
      )}

      {selectedIds.length === 0 ? (
        <p className="text-secondary">{t('multiPlayback.pickToStart', { count: MAX_CAMS })}</p>
      ) : (
        <>
          {/* shared transport */}
          <div className="pb-mc-transport card" data-testid="mc-transport">
            <button className="btn-primary" onClick={togglePlay} disabled={!anyReady} data-testid="mc-playpause" aria-label={playing ? t('multiPlayback.pause') : t('multiPlayback.play')}>
              {playing ? t('multiPlayback.pause') : t('multiPlayback.play')}
            </button>
            <span className="pb-mc-clock" data-testid="mc-clock">{playheadUs !== null ? timeOnly(playheadUs) : '--:--:--'}</span>
            <label className="pb-speed-wrap">
              <span className="text-secondary">{t('playback.speed')}</span>
              <select className="pb-speed" aria-label={t('playback.speedLabel')} value={rate} onChange={(e) => setRate(Number(e.target.value))} data-testid="mc-speed">
                {PLAYBACK_SPEEDS.map((s) => (
                  <option key={s} value={s}>{s}×</option>
                ))}
              </select>
            </label>
          </div>

          {/* video grid */}
          <div className="pb-mc-grid" data-testid="mc-grid" style={{ gridTemplateColumns: readyCams.length <= 1 ? '1fr' : `repeat(${cols}, 1fr)` }}>
            {readyCams.map((c) => (
              <CamCell key={c.device.device_id} device={c.device} clips={c.load.clips} playheadUs={playheadUs} playing={playing} rate={rate} />
            ))}
          </div>

          {!anyReady && anyLoading && <p className="text-secondary">{t('multiPlayback.loading')}</p>}

          {/* shared scrubber（union 覆蓋 + 共用 playhead），下方每台一條 per-gap 覆蓋帶 */}
          {anyReady && playheadUs !== null && (
            <>
              <TimelineStrip range={win} clips={unionRaw} events={[]} playheadUs={playheadUs} onSeek={onSeek} onSeekCommit={onSeek} />
              <div className="pb-mc-lanes" data-testid="mc-lanes">
                {readyCams.map((c) => (
                  <CoverageLane key={c.device.device_id} device={c.device} clips={c.load.clips} range={win} />
                ))}
              </div>
            </>
          )}

          {anyReady && readyCams.every((c) => c.load.clips.length === 0) && (
            <p className="text-secondary">{t('multiPlayback.emptyDay')}</p>
          )}
        </>
      )}
    </section>
  );
}
